import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { EditableFormField, copyAIFieldConfig, NewFormField, getFormFieldChanged } from "Backend";
import SettingsIcon from "@mui/icons-material/Settings";
import { FieldKey, FormField, ReviewConfig } from "generated/graphql";
import { schemas, useForm } from "helpers/FormHelpers";
import { FormCheckbox, FormSelectField, FormTextField } from "helpers/FormHelpersComponents";
import { useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  createAndAttachReviewConfig,
  deleteField,
  deleteReviewConfig,
  selectAllLensEditorReviewConfigs,
  selectLensEditorAiFieldConfigById,
  selectLensEditorForm,
  selectLensEditorLens,
  selectLensEditorLensId,
  selectLensEditorReviewConfigById,
  updateReviewConfig,
} from "redux/slices/lensEditor";
import PSelect from "components/PSelect";
import { Add, AutoAwesome, Delete, Edit, FileUpload } from "@mui/icons-material";
import ReviewConfigForm from "./ReviewConfigForm";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import FieldAIConfigForm from "./FieldAIConfigForm";
import { FieldImport } from "routes/lens-editor/profile-builder/FieldImport";
import CopyToClipboard from "components/CopyToClipboard";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import { LensUpdateSnackbarContext } from "contexts/LensUpdateSnackbarContext";
import PublishFieldModal from "routes/lens-editor/PublishFieldModal";
import { useNavigate } from "react-router";
import { FieldTagConfigForm } from "./FieldTagConfigForm";
import { FormSection } from "./FormSection";

interface FormFieldEditorProps {
  field?: FormField;
  rank: number;
  onSave: (field: EditableFormField) => void;
  isLive: boolean;
  defaultSectionId: number;
}

const FormFieldEditor = (props: FormFieldEditorProps) => {
  const { field, onSave, rank, isLive } = props;
  const allReviewConfigs = useAppSelector(selectAllLensEditorReviewConfigs);
  const allFields = useAppSelector((state) => state.lensEditor.form.fields);
  const confirmCtx = useContext(ConfirmationDialogContext);
  const [showDeleteReviewConfig, setShowDeleteReviewConfig] = useState(false);
  const dispatch = useAppDispatch();
  const form = useAppSelector(selectLensEditorForm);
  const lens = useAppSelector(selectLensEditorLens);
  const snackpatch = useAppDispatchWithSnackbar();
  const aiFieldConfig = useAppSelector((state) => selectLensEditorAiFieldConfigById(state, props.field?.id || 0));
  const [reviewConfigOpen, setReviewConfigOpen] = useState(false);
  const [fieldAiConfigOpen, setFieldAiConfigOpen] = useState(false);
  const lensId = useAppSelector(selectLensEditorLensId);
  const { enqueueLensUpdateTimerSnackbar } = useContext(LensUpdateSnackbarContext);
  const navigate = useNavigate();

  const [importOpen, setImportOpen] = useState(false);
  const closeReviewConfig = () => setReviewConfigOpen(false);
  const closeFieldAiConfig = () => setFieldAiConfigOpen(false);
  const openFieldAiConfig = () => setFieldAiConfigOpen(true);
  const [showPublishFieldModal, setShowPublishFieldModal] = useState(false);
  const newFieldForm = useForm<NewFormField>(
    {
      isValid: false,
      values: {
        name: field?.name || "",
        prompt: field?.prompt || "",
        repeating: field?.repeating || false,
        promptYesNo: field?.promptYesNo === false ? false : true,
        required: field?.required || false,
        key: field?.key || null,
        exportExclude: field?.exportExclude || false,
        reviewConfigId: field?.reviewConfigId,
        description: field?.description,
        groupByCellId: field?.groupByCellId || null,
        analyticsExclude: field?.analyticsExclude || false,
      },
      touched: {
        name: false,
        prompt: false,
        repeating: true,
        promptYesNo: true,
        required: true,
        key: false,
        exportExclude: true,
        reviewConfigId: false,
        description: false,
        groupByCellId: false,
        analyticsExclude: true,
      },
      errors: {
        name: [],
        prompt: [],
        repeating: [],
        promptYesNo: [],
        required: [],
        key: [],
        exportExclude: [],
        reviewConfigId: [],
        description: [],
        groupByCellId: [],
      },
    },
    {
      name: schemas.nonEmpty("Name"),
      prompt: schemas.nonEmpty("Prompt"),
      repeating: { presence: true },
      promptYesNo: { presence: true },
      required: { presence: true },
      key: schemas.basicOptional(),
      exportExclude: { presence: true },
      reviewConfigId: schemas.customType(false),
      description: schemas.basicOptional(),
      groupByCellId: schemas.customType(false),
      analyticsExclude: schemas.customType(false),
    }
  );
  const formState = newFieldForm.formStateHandle[0];

  const isFormChanged = getFormFieldChanged(field, formState.values);
  const save = () => {
    const v = formState.values;

    onSave({
      ...v,
      cells: [],
      rank,
    });
  };

  useEffect(() => {
    if (field) {
      newFieldForm.updateField("name")(field.name);
      newFieldForm.updateField("prompt")(field.prompt);
      newFieldForm.updateField("repeating")(field.repeating);
      newFieldForm.updateField("promptYesNo")(field.promptYesNo);
      newFieldForm.updateField("required")(field.required);
      newFieldForm.updateField("key")(field.key);
      newFieldForm.updateField("exportExclude")(field.exportExclude);
      newFieldForm.updateField("reviewConfigId")(field.reviewConfigId);
      newFieldForm.updateField("description")(field.description);
      newFieldForm.updateField("groupByCellId")(field.groupByCellId);
      newFieldForm.updateField("analyticsExclude")(Boolean(field.analyticsExclude));
    }
  }, [field]);

  const handleAddNewReviewConfig = () => {
    if (field) {
      dispatch(createAndAttachReviewConfig({ fieldId: field.id, name: "Untitled Config" }))
        .unwrap()
        .then((newConfig) => {
          newFieldForm.updateField("reviewConfigId")(newConfig.id);
        });
    }
  };

  const currentReviewConfig = useAppSelector((state) =>
    selectLensEditorReviewConfigById(state, formState.values["reviewConfigId"])
  );

  const saveReviewConfig = async (reviewConfig: ReviewConfig) => {
    await snackpatch(updateReviewConfig({ reviewConfig }), {
      successMessage: "Updated Review Config",
      errorMessage: "Unable to update Review Config",
    });
    closeReviewConfig();
  };

  const handleDeleteReviewConfig = async () => {
    if (currentReviewConfig) {
      const fieldsInUse = allFields.filter((f) => f.reviewConfigId === currentReviewConfig?.id);
      if (fieldsInUse.length === 0) {
        await snackpatch(deleteReviewConfig({ configId: currentReviewConfig.id }), {
          successMessage: "Deleted review config",
          errorMessage: "Unable to delete review config",
        });
        setShowDeleteReviewConfig(false);
      }
    }
  };

  const handleDeleteField = () => {
    confirmCtx.confirm({
      title: "Delete Field",
      prompt: <p className="text-sm">Are you sure you want to delete {field?.name}? This action cannot be undone.</p>,
      confirmButtonText: "Delete",
      callback: () => {
        if (field) {
          return snackpatch(
            deleteField({
              fieldId: field.id,
            }),
            {
              successMessage: "Field deleted",
              errorMessage: "Unable to delete Field",
            }
          ).then(() => {
            navigate(`/lens-editor/${lensId}/profile/sections/`);
            enqueueLensUpdateTimerSnackbar(lensId);
          });
        } else {
          return Promise.resolve();
        }
      },
    });
  };

  return (
    <div className="grid gap-4">
      <FormSection
        label="General"
        actions={
          <div>
            {field && (
              <Tooltip
                title={isLive ? "Disabled due to the profile being live. Please use caution." : "Merge Field JSON"}>
                <span>
                  <IconButton
                    disabled={isLive}
                    onClick={() => {
                      setImportOpen(true);
                    }}>
                    <FileUpload />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        }>
        <FormTextField label="Name" id="name" useForm={newFieldForm} />
        <FormTextField
          label="Prompt"
          id="prompt"
          useForm={newFieldForm}
          InputProps={{
            multiline: true,
            rows: 3,
          }}
        />
        <FormTextField label="Description" id="description" useForm={newFieldForm} />
        <div className="grid grid-cols-2 gap-2">
          <FormSelectField<NewFormField, FieldKey>
            label="Key"
            id="key"
            options={[
              FieldKey.Death,
              FieldKey.PrimaryInjury,
              FieldKey.PrimaryProduct,
              FieldKey.QaStatus,
              FieldKey.SmokingHistory,
            ]}
            useForm={newFieldForm}
          />
          {field && (
            <FormSelectField<NewFormField, number>
              label="Default Group By"
              id="groupByCellId"
              options={field.cells.map((c) => c.id)}
              optionDisplay={(cellId) => field.cells.find((c) => c.id === cellId)?.name ?? ""}
              useForm={newFieldForm}
            />
          )}
        </div>
        <div>
          <FormCheckbox
            label="Repeating"
            id="repeating"
            disabled={field && isLive && field?.repeating === true}
            useForm={newFieldForm}
          />
          <FormCheckbox label="Yes/No Prompt" id="promptYesNo" useForm={newFieldForm} />
          <FormCheckbox
            label="Required"
            id="required"
            disabled={field && isLive && field?.required === false}
            useForm={newFieldForm}
          />
          <FormCheckbox label="Exclude on Export" id="exportExclude" disabled={!isLive} useForm={newFieldForm} />
          <FormCheckbox label="Exclude from Analytics" id="analyticsExclude" useForm={newFieldForm} />
        </div>
        <div className="flex items-center gap-2 justify-end">
          {field && (
            <Button color="secondary" onClick={handleDeleteField}>
              Delete Field
            </Button>
          )}
          {lens.isFamilyPrimary && field && (
            <Button
              variant="outlined"
              disabled={isFormChanged}
              color="primary"
              onClick={() => setShowPublishFieldModal(true)}>
              Publish Field
            </Button>
          )}
          <Button variant="contained" disabled={!formState.isValid} onClick={save}>
            {field ? (isLive ? `Save (Live Update!)` : "Save") : "Create"}
          </Button>
        </div>
      </FormSection>
      {field && (
        <FormSection label="Review Config">
          <div className="">
            <FormControl margin="none" size="small" sx={{ minWidth: "200px" }}>
              <InputLabel variant="outlined" style={{ backgroundColor: "white" }}>
                Review Config
              </InputLabel>
              <PSelect
                startAdornment={<SettingsIcon color="primary" />}
                renderValue={(id) => allReviewConfigs.find((rc) => rc.id === id)?.name}
                value={formState.values["reviewConfigId"] || ""}
                onChange={(evt) => {
                  const value = evt.target.value as number;
                  if (value === -1) {
                    newFieldForm.updateField("reviewConfigId")(undefined);
                  } else {
                    newFieldForm.updateField("reviewConfigId")(value);
                  }
                }}>
                {[
                  <MenuItem value={-1} key="none">
                    None
                  </MenuItem>,
                ].concat(
                  allReviewConfigs
                    .slice(0)
                    .sort((a, b) => (a.name > b.name ? 1 : -1))
                    .map((rc) => (
                      <MenuItem value={rc.id} key={rc.id}>
                        {rc.name}
                      </MenuItem>
                    ))
                )}
              </PSelect>
            </FormControl>
            <IconButton onClick={() => setReviewConfigOpen(true)}>
              <Edit />
            </IconButton>
            <IconButton onClick={() => setShowDeleteReviewConfig(true)} disabled={!currentReviewConfig}>
              <Delete />
            </IconButton>
            <IconButton onClick={handleAddNewReviewConfig}>
              <Add />
            </IconButton>
            {currentReviewConfig && (
              <Dialog open={reviewConfigOpen} onClose={closeReviewConfig}>
                <DialogTitle>Editing Config</DialogTitle>
                <ReviewConfigForm
                  onCancel={closeReviewConfig}
                  onSave={saveReviewConfig}
                  reviewConfig={currentReviewConfig}
                />
              </Dialog>
            )}

            <Dialog
              open={showDeleteReviewConfig}
              transitionDuration={0}
              onClose={() => {
                setShowDeleteReviewConfig(false);
              }}>
              <DialogTitle>Delete Review Config</DialogTitle>
              <DialogContent>
                {allFields.filter((f) => f.reviewConfigId === currentReviewConfig?.id).length > 0 ? (
                  <Box>
                    <Alert severity="error">
                      This review config is currently in use by one or more fields. Please remove this review config
                      from the following fields before deleting.
                    </Alert>

                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          <TableCell>ID</TableCell>
                        </TableRow>
                      </TableHead>

                      {allFields
                        .filter((f) => f.reviewConfigId === currentReviewConfig?.id)
                        .map((f) => (
                          <TableRow key={f.id}>
                            <TableCell>
                              {f.outlinePrefix} {f.name}
                            </TableCell>
                            <TableCell>{f.id}</TableCell>
                          </TableRow>
                        ))}
                    </Table>
                  </Box>
                ) : (
                  <p className="text-xs font-semibold">
                    Are you sure you want to delete this review config? This action cannot be undone.
                  </p>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowDeleteReviewConfig(false)}>Cancel</Button>
                <Button
                  disabled={allFields.filter((f) => f.reviewConfigId === currentReviewConfig?.id).length > 0}
                  onClick={handleDeleteReviewConfig}>
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        </FormSection>
      )}
      {field && (
        <FormSection
          label={
            <div className="flex items-center gap-2">
              AI Config
              <AutoAwesome />
            </div>
          }>
          <div className="flex">
            <Button onClick={openFieldAiConfig} className="p-0 h-8">
              <AutoAwesome sx={{ mr: 1 }} />
              {aiFieldConfig ? "Edit AI Config" : "Create AI Config"}
            </Button>
            {aiFieldConfig && <CopyToClipboard text={copyAIFieldConfig(aiFieldConfig)} />}
          </div>
          <Dialog fullWidth open={fieldAiConfigOpen} onClose={closeFieldAiConfig}>
            <FieldAIConfigForm onCancel={closeFieldAiConfig} fieldId={field && field.id} />
          </Dialog>
        </FormSection>
      )}
      {field && (
        <FormSection label="Tag config">
          <FieldTagConfigForm fieldId={field && field.id} hasAiConfig={!!aiFieldConfig} />
        </FormSection>
      )}
      {importOpen && (
        <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
          <FieldImport
            onClose={() => setImportOpen(false)}
            sourceField={field}
            sourceFormId={form.id}
            defaultSectionId={props.defaultSectionId}
          />
        </Dialog>
      )}
      {showPublishFieldModal && field && (
        <PublishFieldModal
          lensId={lensId}
          fieldId={field.id}
          open={true}
          close={() => setShowPublishFieldModal(false)}
        />
      )}
    </div>
  );
};

export default FormFieldEditor;
